/**
 * Mock 模块：heart_voyage_homepage
 * 多模块共用：BindRelationDrawer、MightValueIntro、HeroHeader、RulesPage、RewardsPage。
 * 默认占领态：双头像 + 情侣飘带。无关系无飘带（header_pair=null）不是默认。
 * @see mockMd/heart_voyage_homepage.md
 */
import {storiesAvatar} from '../components/HeroHeader/stories-images'
import {storiesReward as storiesRewardsPageReward} from '../components/RewardsPage/stories-images'
import {storiesReward} from '../components/VoyageContent/stories-images'
import {
  RelationType,
  type ActivityMemberV2,
  type HeartVoyageFormStageItem,
  type HeartVoyageHomepageData,
  type HeartVoyageShowRewardBlock,
  type TaskReward,
} from '../types'

const MOCK_USER_AVATAR = storiesAvatar
const MOCK_SELF: ActivityMemberV2 = {
  id: 10001,
  name: 'username',
  zy_id: '123456',
  avatar: MOCK_USER_AVATAR,
  gender: 1,
}
const MOCK_REWARD_ICON = storiesReward
const MOCK_REWARDS_PAGE_ICON = storiesRewardsPageReward
export const MOCK_REWARD_DESC_NAME = 'Event Wall of Honor*2day'

const mockBindReward = (id: number): TaskReward => ({
  id,
  icon: MOCK_REWARD_ICON,
  desc: MOCK_REWARD_DESC_NAME,
  privilege_id: 101,
  has_show_effect: true,
  quantity: 2,
  reward_type: 1,
})

const mockBindRewards: TaskReward[] = [
  mockBindReward(8001),
  mockBindReward(8002),
  mockBindReward(8003),
  mockBindReward(8004),
]

let rewardsPageRewardId = 9000

const mockShowReward = (): TaskReward => {
  rewardsPageRewardId += 1
  return {
    id: rewardsPageRewardId,
    icon: MOCK_REWARDS_PAGE_ICON,
    desc: MOCK_REWARD_DESC_NAME,
    privilege_id: 101,
    has_show_effect: true,
    quantity: 2,
    reward_type: 1,
  }
}

const mockShowRewards = (count: number): TaskReward[] =>
  Array.from({length: count}, () => mockShowReward())

const mockRewardBlock = (
  display_order: number,
  title: string,
  subtitle: string,
  specialCount: number,
  normalCount: number
): HeartVoyageShowRewardBlock => ({
  display_order,
  title,
  subtitles: [
    {
      subtitle,
      special_rewards: mockShowRewards(specialCount),
      normal_rewards: mockShowRewards(normalCount),
    },
  ],
})

const SHIP_FORM_TITLE = 'Additional rewards for upgrading ship form'
const PEAK_RANK_TITLES = ['TOP1', 'TOP2', 'TOP3', 'TOP4'] as const

/** 稿面对齐：盲盒 1 + 形态 3；每块 4 special + 4 normal = 8 格 */
const mockShipRewards: HeartVoyageShowRewardBlock[] = [
  mockRewardBlock(1, 'Blind box rewards', '', 4, 4),
  mockRewardBlock(2, SHIP_FORM_TITLE, 'Tier 2 ships', 4, 4),
  mockRewardBlock(3, SHIP_FORM_TITLE, 'Tier 3 ships', 4, 4),
  mockRewardBlock(4, SHIP_FORM_TITLE, 'Tier 4 ships', 4, 4),
]

/** 稿面对齐：三轮；每块 special 2 + normal 2 */
const mockDailyRewards: HeartVoyageShowRewardBlock[] = [
  mockRewardBlock(1, 'Round 1 Challenge Rewards', '', 2, 2),
  mockRewardBlock(2, 'Round 2 Challenge Rewards', '', 2, 2),
  mockRewardBlock(3, 'Round 3 Challenge Rewards', '', 2, 2),
]

/** 稿面对齐：海选 4 档，每档 subtitle 空 */
const mockPeakAuditionRewards: HeartVoyageShowRewardBlock[] =
  PEAK_RANK_TITLES.map((title, index) =>
    mockRewardBlock(index + 1, title, '', 4, 4)
  )

const mockFinalGroupBlock = (
  display_order: number,
  title: string
): HeartVoyageShowRewardBlock => ({
  display_order,
  title,
  subtitles: PEAK_RANK_TITLES.map((subtitle) => ({
    subtitle,
    special_rewards: mockShowRewards(4),
    normal_rewards: mockShowRewards(4),
  })),
})

/** 稿面对齐：2 组，每组 4 档排名文案在 subtitles[].subtitle */
const mockPeakFinalRewards: HeartVoyageShowRewardBlock[] = [
  mockFinalGroupBlock(1, 'Pinnacle Group'),
  mockFinalGroupBlock(2, 'Excellence Group'),
]

/** 稿面 5d 23:59:59 → 5*86400 + 23*3600 + 59*60 + 59 */
export const MOCK_HERO_COUNTDOWN_SEC = 518399

/** 规则页船组：5 形态，level 与接口 form_stages 对齐 */
const mockFormStages: HeartVoyageFormStageItem[] = [
  {level: 1, form_id: 1, total_affinity: 0, rewards: []},
  {
    level: 5,
    form_id: 2,
    total_affinity: 100000,
    rewards: [mockBindReward(8101), mockBindReward(8102)],
  },
  {
    level: 15,
    form_id: 3,
    total_affinity: 1030000,
    rewards: [mockBindReward(8103), mockBindReward(8104)],
  },
  {
    level: 25,
    form_id: 4,
    total_affinity: 3930000,
    rewards: [mockBindReward(8105), mockBindReward(8106)],
  },
  {
    level: 35,
    form_id: 5,
    total_affinity: 27930000,
    rewards: [mockBindReward(8107), mockBindReward(8108)],
  },
]

const homepageShared = {
  err_msg: '',
  activity_id: 12345,
  need_newbie_guide: true,
  countdown: MOCK_HERO_COUNTDOWN_SEC,
  normal_gift_rate: 1,
  special_gift_rate: 1.5,
  special_affinity_rate: 150,
  wheel_game_url: 'https://example.com/wheel',
  gold_miner_game_url: 'https://example.com/miner',
  jump_blind_box_gift_id: 10086,
  audition_start_time: '2026-07-31 00:00:00',
  audition_end_time: '2026-12-31 23:59:59',
  final_start_time: '2026-07-31 00:00:00',
  final_end_time: '2026-12-31 23:59:59',
  max_level_add: 5000,
  form_stages: mockFormStages,
  schedule_segment: 1,
  final_groups: [
    {group_id: 1, group_name: 'Pinnacle Group'},
    {group_id: 2, group_name: 'Excellence Group'},
  ],
  current_final_group_id: 0,
  self: MOCK_SELF,
  pair_key: '10001:10002',
  show_switch_relation: true,
  bind_rewards: mockBindRewards,
  ship_rewards: mockShipRewards,
  daily_rewards: mockDailyRewards,
  peak_audition_rewards: mockPeakAuditionRewards,
  peak_final_rewards: mockPeakFinalRewards,
  current_relation: {
    pair_key: '10001:10002',
    relation_source: 2,
    relation_type: RelationType.Couple,
    peer: {
      id: 10002,
      name: 'Bob',
      zy_id: '654321',
      avatar: MOCK_USER_AVATAR,
      gender: 1,
    },
    affinity: 5200,
    ship_level: 3,
    ship_form: 2,
  },
}

/** 占领：双头像 + 飘带；service 默认 / HeroHeader 默认 VRT */
export const HeartVoyageHomepageOccupiedMock: HeartVoyageHomepageData = {
  ...homepageShared,
  need_newbie_guide: true,
  stage: 2,
  header_pair: {
    rank: 1,
    score: 18888,
    pair_key: '10003:10004',
    ship_level: 8,
    ship_form: 3,
    rel_type: RelationType.Couple,
    member_a: {
      id: 10003,
      name: 'username',
      zy_id: '300003',
      avatar: MOCK_USER_AVATAR,
      gender: 2,
    },
    member_b: {
      id: 10004,
      name: 'username',
      zy_id: '400004',
      avatar: MOCK_USER_AVATAR,
      gender: 1,
    },
  },
}

/** 占位：header_pair 空 → 无飘带；仅预热/正式首日，非默认 */
export const HeartVoyageHomepagePlaceholderMock: HeartVoyageHomepageData = {
  ...homepageShared,
  stage: 1,
  header_pair: null,
}

/** 未绑定：无 current_relation、无 Switch；非默认 VRT */
export const HeartVoyageHomepageUnboundMock: HeartVoyageHomepageData = {
  ...HeartVoyageHomepageOccupiedMock,
  need_newbie_guide: true,
  pair_key: '',
  show_switch_relation: false,
  current_relation: null,
}

/** @see mockMd/heart_voyage_homepage.md · service 默认 = 占领 */
export const HeartVoyageHomepageMock = HeartVoyageHomepageOccupiedMock

/** 决赛赛段：schedule_segment=2 */
export const HeartVoyageHomepageFinalsMock: HeartVoyageHomepageData = {
  ...HeartVoyageHomepageOccupiedMock,
  schedule_segment: 2,
  current_final_group_id: 1,
}

const getPreviewQueryState = () => {
  if (typeof window === 'undefined') {
    return ''
  }
  return new URLSearchParams(window.location.search).get('state') || ''
}

export function resolveHomepageMock(params?: {
  pair_key?: string
  is_pop?: boolean
}): HeartVoyageHomepageData {
  const state = getPreviewQueryState()
  let homepage: HeartVoyageHomepageData = HeartVoyageHomepageMock
  if (state === 'unbound') {
    homepage = HeartVoyageHomepageUnboundMock
  } else if (state === 'finals') {
    homepage = HeartVoyageHomepageFinalsMock
  }

  if (params?.is_pop !== true) {
    return homepage
  }

  return {
    ...homepage,
    new_pending_invites: [],
  }
}

export const HomepageGuideMock = {
  err_msg: '',
  activity_id: 3137,
  stage: 2,
  schedule_segment: 1,
  need_newbie_guide: true,
  countdown: 494581,
  normal_gift_rate: 100,
  special_gift_rate: 1.5,
  special_affinity_rate: 150,
  max_level_add: 1000,
  form_stages: mockFormStages,
  wheel_game_url:
    'hiya://openWindow?fullscreen=2&bg_clear=1&alert_h_ratio=0.8&external=0&other_info=%7B%22from%22:%22chat%22%7D&need_user_info=1&url=http://version-hiya-web-test.youyisia.com/game/lllbbbblllll',
  gold_miner_game_url:
    'hiya://openWindow?fullscreen=2&bg_clear=1&alert_h_ratio=0.8&external=0&other_info=%7B%22from%22:%22chat%22%7D&need_user_info=1&url=http://version-hiya-web-test.youyisia.com/game/gggmmmmeeeee',
  jump_blind_box_gift_id: 0,
  audition_start_time: '2026-08-13 00:00:00',
  audition_end_time: '2026-08-20 00:00:00',
  final_start_time: '2026-08-20 00:00:00',
  final_end_time: '2026-08-25 00:00:00',
  final_groups: [
    {
      group_id: 1,
      group_name: '1组',
    },
    {
      group_id: 2,
      group_name: '2组',
    },
    {
      group_id: 3,
      group_name: '3组',
    },
  ],
  current_final_group_id: 0,
  header_pair: null,
  pair_key: '17015357:18014239',
  show_switch_relation: true,
  self: {
    id: 17015357,
    name: 'hiya8828253',
    zy_id: '17015357',
    avatar:
      'http://me-media-gateway.test03.youyisia.com/accountv2/avatar/id/1145169809/sz/228',
    gender: 2,
  },
  current_relation: {
    pair_key: '17015357:18014239',
    relation_source: 1,
    relation_type: 3,
    peer: {
      id: 18014239,
      name: 'hiya3026021',
      zy_id: '18014239',
      avatar:
        'http://me-media-gateway.test03.youyisia.com/accountv2/avatar/id/1094812226/sz/228',
      gender: 1,
    },
    affinity: 0,
    ship_level: 1,
    ship_form: 1,
  },
  ship_rewards: [
    {
      display_order: 1,
      title: '升级盲盒奖励',
      subtitles: [
        {
          subtitle: '',
          special_rewards: [
            {
              id: 171181,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
          normal_rewards: [
            {
              id: 171182,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
        },
      ],
    },
    {
      display_order: 2,
      title: '升级盲盒奖励',
      subtitles: [
        {
          subtitle: '',
          special_rewards: [
            {
              id: 171183,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
          normal_rewards: [
            {
              id: 171184,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
        },
      ],
    },
    {
      display_order: 3,
      title: '‪2形态船只奖励',
      subtitles: [
        {
          subtitle: '',
          special_rewards: [
            {
              id: 171173,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
          normal_rewards: [
            {
              id: 171177,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
        },
      ],
    },
    {
      display_order: 4,
      title: '‪3形态船只奖励',
      subtitles: [
        {
          subtitle: '',
          special_rewards: [
            {
              id: 171174,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
          normal_rewards: [
            {
              id: 171178,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
        },
      ],
    },
    {
      display_order: 5,
      title: '‪4形态船只奖励',
      subtitles: [
        {
          subtitle: '',
          special_rewards: [
            {
              id: 171175,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
          normal_rewards: [
            {
              id: 171179,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
        },
      ],
    },
    {
      display_order: 6,
      title: '‪5形态船只奖励',
      subtitles: [
        {
          subtitle: '',
          special_rewards: [
            {
              id: 171176,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
          normal_rewards: [
            {
              id: 171180,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
        },
      ],
    },
  ],
  daily_rewards: [
    {
      display_order: 1,
      title: '‪每日挑战第1轮',
      subtitles: [
        {
          subtitle: '',
          special_rewards: [
            {
              id: 171185,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
          normal_rewards: [
            {
              id: 171188,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
        },
      ],
    },
    {
      display_order: 2,
      title: '‪每日挑战第2轮',
      subtitles: [
        {
          subtitle: '',
          special_rewards: [
            {
              id: 171186,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
          normal_rewards: [
            {
              id: 171189,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
        },
      ],
    },
    {
      display_order: 3,
      title: '‪每日挑战第3轮',
      subtitles: [
        {
          subtitle: '',
          special_rewards: [
            {
              id: 171187,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
          normal_rewards: [
            {
              id: 171190,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
        },
      ],
    },
  ],
  peak_audition_rewards: [
    {
      display_order: 1,
      title: '‪海选赛Top1奖励',
      subtitles: [
        {
          subtitle: '',
          special_rewards: [
            {
              id: 171203,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
            {
              id: 171215,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
          normal_rewards: [
            {
              id: 171227,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
        },
      ],
    },
    {
      display_order: 2,
      title: '‪海选赛Top2奖励',
      subtitles: [
        {
          subtitle: '',
          special_rewards: [
            {
              id: 171204,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
            {
              id: 171216,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
          normal_rewards: [
            {
              id: 171228,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
        },
      ],
    },
    {
      display_order: 3,
      title: '‪海选赛Top3奖励',
      subtitles: [
        {
          subtitle: '',
          special_rewards: [
            {
              id: 171205,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
            {
              id: 171217,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
          normal_rewards: [
            {
              id: 171229,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
        },
      ],
    },
  ],
  peak_final_rewards: [
    {
      display_order: 1,
      title: '1组',
      subtitles: [
        {
          subtitle: '‪Top1奖励',
          special_rewards: [
            {
              id: 171206,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
            {
              id: 171218,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
          normal_rewards: [
            {
              id: 171230,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
        },
        {
          subtitle: '‪Top2奖励',
          special_rewards: [
            {
              id: 171207,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
            {
              id: 171219,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
          normal_rewards: [
            {
              id: 171231,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
        },
        {
          subtitle: '‪Top3奖励',
          special_rewards: [
            {
              id: 171208,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
            {
              id: 171220,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
          normal_rewards: [
            {
              id: 171232,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
        },
      ],
    },
    {
      display_order: 2,
      title: '2组',
      subtitles: [
        {
          subtitle: '‪Top1奖励',
          special_rewards: [
            {
              id: 171209,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
            {
              id: 171221,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
          normal_rewards: [
            {
              id: 171233,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
        },
        {
          subtitle: '‪Top2奖励',
          special_rewards: [
            {
              id: 171210,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
            {
              id: 171222,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
          normal_rewards: [
            {
              id: 171234,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
        },
        {
          subtitle: '‪Top3奖励',
          special_rewards: [
            {
              id: 171211,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
            {
              id: 171223,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
          normal_rewards: [
            {
              id: 171235,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
        },
      ],
    },
    {
      display_order: 3,
      title: '3组',
      subtitles: [
        {
          subtitle: '‪Top1奖励',
          special_rewards: [
            {
              id: 171212,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
            {
              id: 171224,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
          normal_rewards: [
            {
              id: 171236,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
        },
        {
          subtitle: '‪Top2奖励',
          special_rewards: [
            {
              id: 171213,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
            {
              id: 171225,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
          normal_rewards: [
            {
              id: 171237,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
        },
        {
          subtitle: '‪Top3奖励',
          special_rewards: [
            {
              id: 171214,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
            {
              id: 171226,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
          normal_rewards: [
            {
              id: 171238,
              icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
              desc: '‪Coins*100',
              privilege_id: null,
              car_effect: null,
              has_show_effect: false,
              quantity: 100,
              reward_type: 4,
            },
          ],
        },
      ],
    },
  ],
  bind_rewards: [
    {
      id: 171172,
      icon: 'https://img01.mehiya.com/imgv2/png/id/173698800235',
      desc: '‪Coins*100',
      privilege_id: null,
      car_effect: null,
      has_show_effect: false,
      quantity: 100,
      reward_type: 4,
    },
  ],
} as HeartVoyageHomepageData
